import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import _debounce from 'lodash/debounce';

const AmountUnitTableCell = ({ getValue, row, column, table, cell }) => {
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const initialValue = getValue();

  const [value, setValue] = useState(initialValue);
  const [amountOptions, setAmountOptions] = useState([]);
  const [amountType, setAmountType] = useState();

  const rowAmountType = useMemo(() => row.getValue('amountType'), [row]);
  const rowImpactLength = useMemo(() => row.getValue('proposedLength'), [row]);
  const rowImpactWidth = useMemo(() => row.getValue('proposedWidth'), [row]);
  const rowImpactAmountUnit = useMemo(() => row.getValue('amountUnit'), [row]);
  const rowImpactActivity = useMemo(() => row.getValue('activity'), [row]);
  const debouncedUpdateRef = useRef();
  const isInitialMount = useRef(true);

  useEffect(() => {
    debouncedUpdateRef.current = _debounce((newValue) => {
      if (tableMeta?.updateData) {
        tableMeta.updateData(row.index, column.id, newValue);
      }
    }, 500);
  }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

  const updateValue = useCallback((newValue) => {
    debouncedUpdateRef.current(newValue);
  }, []);

  const handleChange = (e) => {
    setValue(e.target.value);
    updateValue(e.target.value);
  };

  useEffect(() => {
    if (!rowAmountType) return;
    setAmountType(rowAmountType);
  }, [rowAmountType]);

  useEffect(() => {
    if (amountType?.includes('Volume') && initialValue !== 'Cubic Yards' && rowImpactActivity) {
      value !== 'Cubic Yards' && setValue('Cubic Yards');
      value !== 'Cubic Yards' && tableMeta?.updateData(row.index, column.id, 'Cubic Yards');
    }
  }, [amountOptions, amountType, column.id, row.index, tableMeta, initialValue, value, rowImpactActivity]);

  useEffect(() => {
    if (!rowImpactActivity) return;
    setAmountOptions(columnMeta?.options[0]);
  }, [columnMeta?.options, rowImpactActivity]);

  // if both length and width default amount unit to square feet
  useEffect(() => {
    if (rowImpactLength && rowImpactWidth && !rowImpactAmountUnit) {
      setAmountOptions(columnMeta?.options[0]);
      setValue('Square Feet');
      tableMeta?.updateData(row.index, column.id, 'Square Feet');
    }
  }, [
    amountType,
    columnMeta?.options,
    rowImpactLength,
    rowImpactWidth,
    rowImpactAmountUnit,
    tableMeta,
    row.index,
    column.id,
  ]);

  // Area/Volume amount unit options
  useEffect(() => {
    if (!rowImpactLength && !rowImpactWidth && rowImpactActivity && rowAmountType) {
      const options = amountType?.includes('Area') ? columnMeta?.options[0] : columnMeta?.options[1];
      if (amountOptions?.[0]?.value !== options?.[0]?.value) {
        setAmountOptions(options);
      }
    }
  }, [
    amountType,
    columnMeta?.options,
    amountOptions,
    rowImpactLength,
    rowImpactWidth,
    rowImpactActivity,
    rowAmountType,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (amountType && !amountType?.includes('Volume') && value === 'Cubic Yards') {
      setValue('');
      tableMeta?.updateData(row.index, column.id, '');
      tableMeta?.updateData(row.index, 'proposedAmount', null);
    }
  }, [amountType, value, column.id, row.index, tableMeta]);

  return (
    <select
      aria-label='Area Units'
      disabled={columnMeta?.readOnly}
      id={cell.id}
      onChange={handleChange}
      required={columnMeta?.required}
      style={{ width: '100%', borderColor: 'hsl(0, 0%, 80%)' }}
      value={value ?? ''}
    >
      <option key={0} value='' className='none' style={{ display: 'none' }}>
        -- Select a value --
      </option>
      {amountOptions?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export default AmountUnitTableCell;
